/**
 * Pure helpers behind the shared step views (`step-view-preference.tsx`,
 * `step-views.tsx`, and the `stepByStepGrid` block in
 * `portable-text-renderer.tsx`).
 *
 * Plain `.ts` on purpose: Server Components, scripts and unit tests can import
 * these without pulling in a `'use client'` module (calling a function
 * exported from a client module crashes an RSC render).
 *
 * Flogger chapter-model migration PR-2a (design doc
 * `docs/design/flogger-components-chapter-model-design.md`, sections 2.4, 3, 8).
 * The behaviour changes (prefix strip, carousel keys) are OFF unless a caller
 * opts in, so tutorial and CAS pages look and behave as before. The asset alt
 * in every mode and the carousel's accessibility semantics are unconditional
 * (design doc section 3: "Improve (all modes)"); they change no visible output.
 */

/* ------------------------------------------------------------------ */
/*  View mode                                                          */
/* ------------------------------------------------------------------ */

export type StepViewMode = 'list' | 'grid' | 'carousel'

/** The mode a reader sees when there is no saved choice and no `defaultMode`. */
export const STEP_VIEW_FALLBACK_MODE: StepViewMode = 'list'

export function isStepViewMode(value: unknown): value is StepViewMode {
  return value === 'list' || value === 'grid' || value === 'carousel'
}

/**
 * The mode a step view starts in.
 *
 * Precedence: the account preference from the server
 * (`profiles.step_view_default`) → the browser's saved choice (localStorage)
 * → the page's `defaultMode` → `'list'`.
 *
 * `defaultMode` applies only to a reader with **no saved choice**. It is never
 * written anywhere: only an explicit change of view (the picker) saves, and
 * that save stays account-wide, as before (design doc D8).
 */
export function resolveInitialStepViewMode({
  serverMode,
  storedMode,
  defaultMode,
}: {
  serverMode?: StepViewMode | null
  /** Raw localStorage value; anything that is not a mode is ignored. */
  storedMode?: unknown
  defaultMode?: StepViewMode | null
}): StepViewMode {
  if (isStepViewMode(serverMode)) return serverMode
  if (isStepViewMode(storedMode)) return storedMode
  if (isStepViewMode(defaultMode)) return defaultMode
  return STEP_VIEW_FALLBACK_MODE
}

/**
 * Opt-in step-view features, set on `StepViewProvider` (or ArticleView's
 * `stepViewOptions`) and read by the `stepByStepGrid` block. All off by default.
 */
export interface StepViewOptions {
  /**
   * Hide a leading "N." step-number prefix when N equals the number the view
   * draws for that step (design doc 2.4). Stored text is not changed.
   */
  stripMatchingNumberPrefix?: boolean
  /**
   * ←/→ step the carousel while focus is inside it, or while the pointer is
   * over it and the focused element (if any) does not use arrow keys itself
   * (see `hoverKeysAllowed`). No page-wide key handler.
   */
  carouselKeyboardNav?: boolean
}

/* ------------------------------------------------------------------ */
/*  Matching-number prefix strip (design doc 2.4)                      */
/* ------------------------------------------------------------------ */

/**
 * The prefix, as in design doc 2.4: `^\s*(\d+)([A-Za-z]?)\.\s+`. The letter is
 * its own group so a lettered prefix ("4A.") can be recognised and left alone:
 * the strip applies only when the letter group is empty AND the number equals
 * the step's display number. (DuchyMobile PR-M copies this exact rule.)
 */
export const STEP_NUMBER_PREFIX_RE = /^\s*(\d+)([A-Za-z]?)\.\s+/

export interface StepNumberPrefix {
  number: number
  /** '' when the prefix has no letter. */
  letter: string
  /** Characters the prefix covers, including surrounding whitespace. */
  length: number
}

export function parseStepNumberPrefix(text: string): StepNumberPrefix | null {
  const m = STEP_NUMBER_PREFIX_RE.exec(text)
  if (!m) return null
  return { number: Number(m[1]), letter: m[2], length: m[0].length }
}

/**
 * How many leading characters to hide: the prefix length when its number
 * equals `displayNumber` and it has no letter, else 0. Mismatched numbers
 * (continued numbering) and lettered prefixes ("4A.") carry meaning, so they
 * stay. A null `displayNumber` (an unnumbered step) never strips.
 */
export function matchingStepNumberPrefixLength(
  text: string,
  displayNumber: number | null | undefined,
): number {
  if (displayNumber == null || !Number.isFinite(displayNumber)) return 0
  const prefix = parseStepNumberPrefix(text)
  if (!prefix || prefix.letter !== '' || prefix.number !== displayNumber) return 0
  return prefix.length
}

export function stripMatchingStepNumberPrefix(
  text: string,
  displayNumber: number | null | undefined,
): string {
  const n = matchingStepNumberPrefixLength(text, displayNumber)
  return n > 0 ? text.slice(n) : text
}

/* eslint-disable @typescript-eslint/no-explicit-any */
type Block = Record<string, any>

/**
 * Apply the strip to a portable-text block array. Only the FIRST block is
 * looked at, and only its leading run of text spans (the prefix can be split
 * across spans, e.g. "3." bold + "\t" plain). Returns the input unchanged
 * (same reference) when nothing matches; never mutates it.
 */
export function stripMatchingStepNumberPrefixFromBlocks<T>(
  blocks: T,
  displayNumber: number | null | undefined,
): T {
  if (!Array.isArray(blocks) || blocks.length === 0) return blocks
  const first = blocks[0] as Block | null | undefined
  if (!first || first._type !== 'block' || !Array.isArray(first.children)) return blocks
  const children = first.children as Block[]

  let leadingText = ''
  let leadingSpans = 0
  for (const child of children) {
    if (!child || child._type !== 'span' || typeof child.text !== 'string') break
    leadingText += child.text
    leadingSpans += 1
  }
  if (leadingSpans === 0) return blocks

  let remaining = matchingStepNumberPrefixLength(leadingText, displayNumber)
  if (remaining === 0) return blocks

  const nextChildren: Block[] = []
  children.forEach((child, i) => {
    if (i >= leadingSpans || remaining === 0) {
      nextChildren.push(child)
      return
    }
    const text = child.text as string
    if (text.length <= remaining) {
      remaining -= text.length
      return // the whole span was prefix
    }
    nextChildren.push({ ...child, text: text.slice(remaining) })
    remaining = 0
  })
  if (nextChildren.length === 0) {
    // The block was only the prefix: keep one empty span so it stays valid.
    nextChildren.push({ ...children[0], text: '' })
  }
  return [{ ...first, children: nextChildren }, ...blocks.slice(1)] as T
}

/**
 * Apply the strip to a localePortableText value (`{ en: [...], ja: [...] }`)
 * or a bare block array, locale by locale. Each locale is judged on its own
 * text, so a translation whose prefix does not match keeps it.
 */
export function stripMatchingStepNumberPrefixFromLocaleText<T>(
  value: T,
  displayNumber: number | null | undefined,
): T {
  if (value == null || displayNumber == null) return value
  if (Array.isArray(value)) return stripMatchingStepNumberPrefixFromBlocks(value, displayNumber)
  if (typeof value !== 'object') return value
  let changed = false
  const out: Record<string, unknown> = {}
  for (const [lang, blocks] of Object.entries(value as Record<string, unknown>)) {
    const next = stripMatchingStepNumberPrefixFromBlocks(blocks, displayNumber)
    if (next !== blocks) changed = true
    out[lang] = next
  }
  return changed ? (out as T) : value
}
/* eslint-enable @typescript-eslint/no-explicit-any */

/* ------------------------------------------------------------------ */
/*  Image button accessible name                                       */
/* ------------------------------------------------------------------ */

/**
 * Accessible name for a click-to-enlarge image button. A button's aria-label
 * replaces its content as the accessible name (and a button's children are
 * presentational), so the img alt is never announced unless it is part of the
 * label. Empty alt → the prefix alone.
 */
export function fullSizeButtonLabel(
  alt: string | null | undefined,
  prefix = 'View image full size',
): string {
  const a = (alt || '').trim()
  return a ? `${prefix}: ${a}` : prefix
}

/* ------------------------------------------------------------------ */
/*  Carousel index + scoped keyboard navigation                        */
/* ------------------------------------------------------------------ */

/** Clamp a carousel index into `[0, total - 1]` (0 when there are no steps). */
export function clampStepIndex(index: number, total: number): number {
  if (!Number.isFinite(index) || total <= 0) return 0
  return Math.max(0, Math.min(total - 1, Math.trunc(index)))
}

/** The subset of a DOM element the key helpers look at. */
export interface KeyTargetLike {
  tagName?: string
  isContentEditable?: boolean
  /** An `<input>`'s type (`HTMLInputElement.type`). */
  type?: string
}

/**
 * `<input>` types that do nothing with ←/→ (no caret, no value to step), so a
 * carousel may still take the arrow keys while one has focus: the step
 * progress checkbox in the carousel footer (flogger PR-2b) is the case that
 * matters. Radio and range inputs DO use arrows and stay editable.
 */
const ARROW_INERT_INPUT_TYPES: ReadonlySet<string> = new Set([
  'checkbox',
  'button',
  'submit',
  'reset',
  'image',
])

/** Accepts any event target (a DOM `EventTarget` has no static `tagName`). */
export function isEditableKeyTarget(target: unknown): boolean {
  if (!target || typeof target !== 'object') return false
  const t = target as KeyTargetLike
  const tag = (typeof t.tagName === 'string' ? t.tagName : '').toUpperCase()
  if (tag === 'INPUT') {
    const type = typeof t.type === 'string' ? t.type.toLowerCase() : ''
    return !ARROW_INERT_INPUT_TYPES.has(type)
  }
  return tag === 'TEXTAREA' || tag === 'SELECT' || t.isContentEditable === true
}

/** The subset of a KeyboardEvent the key helpers look at. */
export interface KeyEventLike {
  key: string
  altKey?: boolean
  ctrlKey?: boolean
  metaKey?: boolean
  shiftKey?: boolean
  defaultPrevented?: boolean
  target?: unknown
}

/**
 * -1 for ←, +1 for →, 0 for anything the carousel must leave alone: other
 * keys, modifier combos (Alt/Cmd+← is history navigation), an event someone
 * else already handled, and keys typed into a form field.
 */
export function carouselKeyDelta(e: KeyEventLike): -1 | 0 | 1 {
  if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return 0
  if (e.altKey || e.ctrlKey || e.metaKey || e.shiftKey) return 0
  if (e.defaultPrevented) return 0
  if (isEditableKeyTarget(e.target)) return 0
  return e.key === 'ArrowLeft' ? -1 : 1
}

/**
 * Focused elements that use ←/→ themselves (composite widgets, media, range
 * inputs) or that belong to another carousel. While one of these has focus, a
 * hovered carousel leaves the arrow keys to it.
 */
export const ARROW_KEY_OWNER_SELECTOR = [
  '[role="tablist"]',
  '[role="tab"]',
  '[role="slider"]',
  '[role="scrollbar"]',
  '[role="spinbutton"]',
  '[role="listbox"]',
  '[role="option"]',
  '[role="combobox"]',
  '[role="menu"]',
  '[role="menubar"]',
  '[role="menuitem"]',
  '[role="menuitemradio"]',
  '[role="menuitemcheckbox"]',
  '[role="radiogroup"]',
  '[role="radio"]',
  '[role="grid"]',
  '[role="treegrid"]',
  '[role="tree"]',
  '[role="treeitem"]',
  '[role="toolbar"]',
  // Step carousels mark themselves with a data attribute because their
  // aria-roledescription is localized; other carousels use the English word.
  '[data-steps-carousel]',
  '[aria-roledescription="carousel"]',
  'input[type="radio"]',
  'input[type="range"]',
  'video',
  'audio',
  'iframe',
].join(',')

/** The subset of a DOM element `hoverKeysAllowed` looks at. */
export interface FocusTargetLike extends KeyTargetLike {
  closest?: (selector: string) => unknown
}

/**
 * Whether a HOVERED carousel may act on ←/→ given what has focus.
 *
 * Yes when nothing is focused (body / html), and when an ordinary control is
 * focused (a button such as the view picker, a TOC link): those do nothing
 * with arrow keys, so the old page-wide bench keys kept working there and the
 * scoped handler must too (design doc section 3, "active on focus or hover").
 * No when a text field is focused, or when the focused element sits in a
 * widget that uses arrow keys itself or inside another carousel
 * (`ARROW_KEY_OWNER_SELECTOR`).
 */
export function hoverKeysAllowed(
  activeElement: FocusTargetLike | null | undefined,
): boolean {
  if (!activeElement) return true
  const tag = (activeElement.tagName || '').toUpperCase()
  if (tag === 'BODY' || tag === 'HTML') return true
  if (isEditableKeyTarget(activeElement)) return false
  if (typeof activeElement.closest === 'function' && activeElement.closest(ARROW_KEY_OWNER_SELECTOR)) {
    return false
  }
  return true
}
